import { put, takeLatest } from "redux-saga/effects";
import { AxiosError, AxiosResponse } from "axios";
import { axiosInstance } from "../../utils/AxiosInstance";
import {
  getAllOrdersFailure,
  getAllOrdersSuccess,
  getOrderDetailsFailure,
  getOrderDetailsSuccess,
  getAdjustmentTypesFailure,
  getAdjustmentTypesSuccess,
  getProjectsFailure,
  getProjectsSuccess,
  getOrderIssuesSuccess,
  getOrderIssuesFailure,
  getIssueDetailsSuccess,
  getIssueDetailsFailure,
  getInvoicesSuccess,
  getInvoicesFailure,
  getInvoiceOrdersFailure,
  getInvoiceOrdersSuccess,
  getResultsDeliveredSuccess,
  getResultsDeliveredFailure,
  getOrderInspectionSuccess,
  getOrderInspectionFailure,
  getAssignableInspectionFirmsSuccess,
  getAssignableInspectionFirmsFailure,
  getOrderHistorySuccess,
  getOrderHistoryFailure,
  getOrderInspectionFormFailure,
  getOrderInspectionFormSuccess,
  getSearchOrdersSuccess,
  getSearchOrdersFailure,
  getOrderIssuesCountSuccess,
  getOrderIssuesCountFailure,
  getCreateIssueSearchOrderSuccess,
  getCreateIssueSearchOrderFailure,
  getQCModificationSuccess,
  getQCModificationFailure,
  getOrderAttachmentsSuccess,
  getOrderAttachmentsFailure,
} from "./slice";
import { ORDERS_REQUEST } from "./types";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function* getProjects(action: any) {
  const params = action.payload;
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/projects/list`,
      {
        params,
      }
    );
    yield put(getProjectsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getProjectsFailure({ error: err.response?.data }));
  }
}

function* getAdjustmentTypes(action: any) {
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/billing/adjustment-types`
    );
    yield put(getAdjustmentTypesSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getAdjustmentTypesFailure({ error: err.response?.data }));
  }
}

function* getAllOrders(action: any) {
  const params = {
    ...action.payload,
  };
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders`,
      {
        params,
      }
    );
    yield put(getAllOrdersSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getAllOrdersFailure({ error: err.response?.data }));
  }
}

function* getOrderDetails(action: any) {
  try {
    const { id } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${id}`
    );
    yield put(getOrderDetailsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getOrderDetailsFailure({ error: err.response?.data }));
  }
}

function* getOrderIssues(action: any) {
  const params = {
    ...action.payload,
  };

  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/issues`,
      {
        params,
      }
    );
    yield put(getOrderIssuesSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getOrderIssuesFailure({ error: err.response?.data }));
  }
}

function* getIssueDetails(action: any) {
  try {
    const { issueId } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/issues/${issueId}`
    );
    yield put(getIssueDetailsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getIssueDetailsFailure({ error: err.response?.data }));
  }
}

function* getInvoices(action: any) {
  const params = {
    ...action.payload,
  };
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/invoices`,
      {
        params,
      }
    );
    yield put(getInvoicesSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getInvoicesFailure({ error: err.response?.data }));
  }
}

function* getResultsDelivered(action: any) {
  const params = {
    ...action.payload,
  };
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/result-delivered`,
      {
        params,
      }
    );
    yield put(getResultsDeliveredSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getResultsDeliveredFailure({ error: err.response?.data }));
  }
}

function* getInvoiceOrders(action: any) {
  const { invoiceId, params } = action.payload;

  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/invoices/${invoiceId}`,
      {
        params,
      }
    );
    yield put(getInvoiceOrdersSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getInvoiceOrdersFailure({ error: err.response?.data }));
  }
}

function* getOrderInspection(action: any) {
  try {
    const { orderId } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${orderId}/inspection`
    );
    yield put(getOrderInspectionSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getOrderInspectionFailure({ error: err.response?.data }));
  }
}

function* getAssignableInspectionFirms(action: any) {
  try {
    const { orderId, ...params } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${orderId}/inspector-firms/assignable`,
      { params }
    );
    yield put(
      getAssignableInspectionFirmsSuccess({ response: response?.data })
    );
  } catch (error) {
    const err = error as AxiosError;
    yield put(
      getAssignableInspectionFirmsFailure({ error: err.response?.data })
    );
  }
}

function* getOrderHistory(action: any) {
  try {
    const { orderId } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${orderId}/history`
    );
    yield put(getOrderHistorySuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getOrderHistoryFailure({ error: err.response?.data }));
  }
}

function* getOrderInspectionForm(action: any) {
  try {
    const { orderId } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${orderId}/inspection/sections`
    );
    yield put(getOrderInspectionFormSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getOrderInspectionFormFailure({ error: err.response?.data }));
  }
}

function* searchOrders(action: any) {
  const params = {
    ...action.payload,
  };
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/search`,
      {
        params,
      }
    );
    yield put(getSearchOrdersSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getSearchOrdersFailure({ error: err.response?.data }));
  }
}

function* getOrderIssuesCount(action: any) {
  try {
    const { orderId } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${orderId}/issues/count`
    );
    yield put(getOrderIssuesCountSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getOrderIssuesCountFailure({ error: err.response?.data }));
  }
}

function* createIssueSearchOrders(action: any) {
  const params = {
    ...action.payload,
  };
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/search/issue-create`,
      {
        params,
      }
    );
    yield put(getCreateIssueSearchOrderSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getCreateIssueSearchOrderFailure({ error: err.response?.data }));
  }
}

function* getQCModification(action: any) {
  try {
    const { orderId } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${orderId}/inspection/qc-modification`
    );
    yield put(getQCModificationSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getQCModificationFailure({ error: err.response?.data }));
  }
}

function* getOrderAttachments(action: any) {
  try {
    const { orderId } = action.payload;
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/orders/${orderId}/attachments`
    );
    yield put(getOrderAttachmentsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getOrderAttachmentsFailure({ error: err.response?.data }));
  }
}

export function* ordersSaga() {
  yield takeLatest(`${ORDERS_REQUEST}/getProjectsRequest`, getProjects);
  yield takeLatest(
    `${ORDERS_REQUEST}/getAdjustmentTypesRequest`,
    getAdjustmentTypes
  );
  yield takeLatest(`${ORDERS_REQUEST}/getAllOrdersRequest`, getAllOrders);
  yield takeLatest(`${ORDERS_REQUEST}/getOrderDetailsRequest`, getOrderDetails);
  yield takeLatest(`${ORDERS_REQUEST}/getOrderIssuesRequest`, getOrderIssues);
  yield takeLatest(`${ORDERS_REQUEST}/getIssueDetailsRequest`, getIssueDetails);
  yield takeLatest(`${ORDERS_REQUEST}/getInvoicesRequest`, getInvoices);
  yield takeLatest(
    `${ORDERS_REQUEST}/getResultsDeliveredRequest`,
    getResultsDelivered
  );
  yield takeLatest(
    `${ORDERS_REQUEST}/getInvoiceOrdersRequest`,
    getInvoiceOrders
  );
  yield takeLatest(
    `${ORDERS_REQUEST}/getOrderInspectionRequest`,
    getOrderInspection
  );
  yield takeLatest(
    `${ORDERS_REQUEST}/getAssignableInspectionFirmsRequest`,
    getAssignableInspectionFirms
  );
  yield takeLatest(`${ORDERS_REQUEST}/getOrderHistoryRequest`, getOrderHistory);
  yield takeLatest(
    `${ORDERS_REQUEST}/getOrderInspectionFormRequest`,
    getOrderInspectionForm
  );
  yield takeLatest(`${ORDERS_REQUEST}/getSearchOrdersRequest`, searchOrders);
  yield takeLatest(
    `${ORDERS_REQUEST}/getOrderIssuesCountRequest`,
    getOrderIssuesCount
  );
  yield takeLatest(
    `${ORDERS_REQUEST}/getCreateIssueSearchOrderRequest`,
    createIssueSearchOrders
  );
  yield takeLatest(
    `${ORDERS_REQUEST}/getQCModificationRequest`,
    getQCModification
  );
  yield takeLatest(
    `${ORDERS_REQUEST}/getOrderAttachmentsRequest`,
    getOrderAttachments
  );
}
